import { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus, Search, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { fetchLocations, deleteLocation, getLocationDeleteImpact, type Location } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { AddLocationForm } from '@/components/ui/AddLocationForm';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useLiveReload } from '@/components/LiveUpdatesProvider';
import { useStartTimeouts } from '@/hooks/useStartTimeouts';
import { TimeoutCell } from '@/components/ui/TimeoutCell';
import type { StartTimeoutLocationRow } from '@evi/shared';
import styles from './LocationsPage.module.css';

interface LocationsPageProps {
  onNavigate: (path: string) => void;
}

const LOCATION_TYPE_LABELS: Record<string, string> = {
  residential: 'Residential',
  multifamily: 'Multi-Family',
  commercial: 'Commercial',
  public: 'Public',
  fleet: 'Fleet',
};

export function LocationsPage({ onNavigate }: LocationsPageProps) {
  useRecentRoute('Sites');
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [addModalOpen, setAddModalOpen] = useState(false);

  // `silent` (live reloads) refetches in place: no spinner, and a failed
  // background refetch keeps the rows already shown.
  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const res = await fetchLocations({ page, limit: 20, search: search || undefined });
      setLocations(res.data);
      setTotalPages(res.meta.pages);
      setTotal(res.meta.total);
    } catch (err) {
      if (!silent) setError(err instanceof Error ? err.message : 'Failed to load locations');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  // Live (28 Oct 2026): a charger added, moved or switched refreshes the
  // charger counts in place; the start-timeout column is live on its own.
  useLiveReload({ kinds: ['charger'], reload: () => { void load(true); } });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const handleDeleteLocation = async (loc: Location): Promise<void> => {
    try {
      const pre = await getLocationDeleteImpact(loc._id);
      if (pre.blocked) {
        alert(pre.block?.reason ?? 'This location cannot be deleted right now.');
        return;
      }
      const im = pre.impact;
      const msg = [
        `Delete location "${im?.name ?? loc.name}"?`,
        '',
        'This deactivates the location (soft delete -- history is kept) and:',
        `  • deactivates ${im?.chargers_to_deactivate ?? 0} charger(s) and ${im?.circuits_to_deactivate ?? 0} circuit(s) here`,
        `  • ${im?.sessions_preserved ?? 0} past session(s) are preserved`,
        '',
        'Continue?',
      ].join('\n');
      if (!confirm(msg)) return;
      await deleteLocation(loc._id);
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete location.');
    }
  };

  // Columns describe both the desktop table shape and the mobile card
  // shape (via the `mobile:` marker). Primary = card heading, secondary
  // = subtitle, body = label/value rows inside the card.
  // Two-Minute Window 4.5 — app-start timeout rate per site (30d):
  // the sum of the site's chargers, flagged above 40% of 10+ starts.
  const timeoutsQ = useStartTimeouts('30d');
  const timeoutByLocation = new Map<string, StartTimeoutLocationRow>(
    (timeoutsQ.data?.locations ?? []).map((r) => [r.location_id, r]),
  );

  const columns: Column<Location>[] = [
    {
      key: 'name',
      header: 'Name',
      mobile: 'primary',
      cell: (loc) => (
        <span className={styles.nameCell}>
          <MapPin size={14} className={styles.rowIcon} />
          {loc.name}
        </span>
      ),
    },
    {
      key: 'address',
      header: 'Address',
      mobile: 'secondary',
      cell: (loc) => (
        <span className={styles.addressCell}>
          {loc.address.city}, {loc.address.state}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      mobile: 'body',
      cell: (loc) => (
        <span className={styles.typeBadge}>
          {LOCATION_TYPE_LABELS[loc.type] ?? loc.type}
        </span>
      ),
    },
    {
      key: 'chargers',
      header: 'Chargers',
      mobile: 'body',
      align: 'right',
      cell: (loc) => loc.charger_count,
    },
    {
      key: 'timeouts',
      header: 'Timeouts (30d)',
      mobile: 'body',
      cell: (loc) => <TimeoutCell row={timeoutByLocation.get(loc._id)} />,
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'body',
      cell: (loc) => (
        <span className={styles.statusCell}>
          <span className={`${styles.statusDot} ${loc.is_active ? styles.statusActive : styles.statusInactive}`} />
          {loc.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      mobile: 'body',
      align: 'right',
      cell: (loc) => (
        <button
          type="button"
          title="Delete (deactivate) this location"
          onClick={(e) => { e.stopPropagation(); void handleDeleteLocation(loc); }}
          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
        >
          <Trash2 size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <MapPin size={18} />
          </div>
          <div>
            <h1 className={styles.title}>Sites</h1>
            <p className={styles.subtitle}>{total} site{total !== 1 ? 's' : ''} in your network</p>
          </div>
        </div>
        <button className={styles.primaryBtn} onClick={() => setAddModalOpen(true)}>
          <Plus size={16} />
          Add Site
        </button>
      </div>

      {/* Resident-Managed Chargers card removed in Phase 3 of
          the tenant→location migration — the toggle now lives
          per-Location on each Location's detail page (see
          LocationResidentManagedCard inside LocationDetailPage).
          The legacy ResidentChargersCard component file stays
          in the codebase during the soak period; Phase 4
          deletes it alongside the Tenant.allow_resident_self_-
          registration field. */}

      {/* Search bar */}
      <form className={styles.searchBar} onSubmit={handleSearch}>
        <Search size={16} className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search locations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchInput}
        />
      </form>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <p className={styles.loading}>Loading...</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={locations}
          rowKey={(loc) => loc._id}
          onRowClick={(loc) => onNavigate(`/locations/${loc._id}`)}
          emptyMessage="No locations found"
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft size={16} /> Prev
          </button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      <Modal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Add Site"
      >
        <AddLocationForm
          onSuccess={() => { setAddModalOpen(false); setPage(1); load(); }}
          onCancel={() => setAddModalOpen(false)}
        />
      </Modal>
    </div>
  );
}
